// Data for /tools/best-ai-cdp/
// Owner: Dmitrii Serg
// Prices verified from vendor sites. May 2026

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TldrRow {
  toolSlug: string;
  toolName: string;
  bestFor: string;
  cdpType: "Conversational" | "Developer" | "Enterprise" | "Ecommerce" | "Composable";
  priceLabel: string;
  freeTrial: boolean;
  g2Rating: string;
  featured: boolean;
  profileUrl?: string;
}

export interface CriterionCard {
  n: number;
  title: string;
  body: string;
}

export interface CdpTypeRow {
  cdpType: string;
  aiCapabilities: string;
  primaryData: string;
  activation: string;
  bestFor: string;
}

export interface PricingFacts {
  pricingModel: string;
  freeTrial: string;
  enterpriseNote?: string;
}

export interface TopTool {
  position: number;
  toolSlug: string;
  toolName: string;
  bestFor: string;
  intro: string;
  pros: string[];
  cons: string[];
  startingPrice: string;
  pricingFacts: PricingFacts;
  outcomesLine?: string;
  g2Rating: string;
  profileUrl?: string;
}

export interface MoreTool {
  toolSlug: string;
  toolName: string;
  cdpType: "Conversational" | "Developer" | "Enterprise" | "Ecommerce" | "Composable";
  tagline: string;
  startingPrice: string;
  g2Rating: string;
  pros: string[];
  cons: string[];
  bestFor: string;
  profileUrl?: string;
}

export interface UseCaseCard {
  id: string;
  color: string;
  labelColor?: string;
  label: string;
  heading: string;
  body: string;
  tools: string[];
}

export interface StageCard {
  stage: string;
  label: string;
  color: string;
  heading: string;
  body: string;
  tools: string[];
  watch_out: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

// ─── TL;DR rows (10 tools) ────────────────────────────────────────────────────

export const TLDR_ROWS: TldrRow[] = [
  {
    toolSlug: "dashly",
    toolName: "Dashly",
    bestFor: "Conversational AI CDP for B2B SaaS",
    cdpType: "Conversational",
    priceLabel: "$500/mo",
    freeTrial: false,
    g2Rating: "4.8",
    featured: true,
    profileUrl: "/tools/ai-sdr-tools/dashly/",
  },
  {
    toolSlug: "segment",
    toolName: "Segment (Twilio)",
    bestFor: "Developer CDP with AI Predictions",
    cdpType: "Developer",
    priceLabel: "Free / from $120/mo",
    freeTrial: true,
    g2Rating: "4.6",
    featured: false,
  },
  {
    toolSlug: "hightouch",
    toolName: "Hightouch",
    bestFor: "Composable CDP + AI audience builder",
    cdpType: "Composable",
    priceLabel: "Free / from $350/mo",
    freeTrial: true,
    g2Rating: "4.7",
    featured: false,
  },
  {
    toolSlug: "adobe-rtcdp",
    toolName: "Adobe Real-Time CDP",
    bestFor: "Enterprise AI CDP for Adobe stack",
    cdpType: "Enterprise",
    priceLabel: "Custom ($40K+/year)",
    freeTrial: false,
    g2Rating: "4.1",
    featured: false,
  },
  {
    toolSlug: "bloomreach",
    toolName: "Bloomreach",
    bestFor: "E-commerce CDP with personalization engine",
    cdpType: "Ecommerce",
    priceLabel: "Custom",
    freeTrial: false,
    g2Rating: "4.7",
    featured: false,
  },
  {
    toolSlug: "klaviyo",
    toolName: "Klaviyo",
    bestFor: "E-commerce CDP with AI send-time",
    cdpType: "Ecommerce",
    priceLabel: "Free / from $45/mo",
    freeTrial: true,
    g2Rating: "4.6",
    featured: false,
  },
  {
    toolSlug: "salesforce-data-cloud",
    toolName: "Salesforce Data Cloud",
    bestFor: "CRM-native enterprise CDP with Einstein AI",
    cdpType: "Enterprise",
    priceLabel: "Custom",
    freeTrial: false,
    g2Rating: "4.2",
    featured: false,
  },
  {
    toolSlug: "mparticle",
    toolName: "mParticle",
    bestFor: "Enterprise CDP for mobile-first teams",
    cdpType: "Enterprise",
    priceLabel: "Custom",
    freeTrial: false,
    g2Rating: "4.3",
    featured: false,
  },
  {
    toolSlug: "tealium",
    toolName: "Tealium",
    bestFor: "Enterprise CDP with tag management + AI",
    cdpType: "Enterprise",
    priceLabel: "Custom",
    freeTrial: false,
    g2Rating: "4.5",
    featured: false,
  },
  {
    toolSlug: "simon-data",
    toolName: "Simon Data",
    bestFor: "Marketer-friendly CDP with ML audiences",
    cdpType: "Enterprise",
    priceLabel: "Custom",
    freeTrial: false,
    g2Rating: "4.5",
    featured: false,
  },
];

// ─── Evaluation criteria cards ────────────────────────────────────────────────

export const CRITERIA: CriterionCard[] = [
  {
    n: 1,
    title: "Real-time data ingestion",
    body: "Does the CDP process events as they happen, or does it batch them into hourly loads? For inbound conversion, a 10-minute lag on a lead visiting the pricing page makes the AI agent's context stale before the conversation starts.",
  },
  {
    n: 2,
    title: "AI segmentation quality",
    body: "Predictive models that score users by churn probability, conversion likelihood, or CLV are worth more than rule-based segments. Look for documented model accuracy, not just a mention of 'AI-powered segmentation' in the marketing copy.",
  },
  {
    n: 3,
    title: "Activation channels",
    body: "A CDP that collects data but only exports to a data warehouse is a pipeline tool, not an activation platform. Check whether the CDP can trigger a chat conversation, send an email, push to CRM, or update an ad audience in real time.",
  },
  {
    n: 4,
    title: "Pricing transparency",
    body: "Enterprise CDPs almost universally hide pricing behind a sales call. If the vendor will not publish a starting price, budget 3x whatever the initial quote is. Transparent self-serve pricing matters for teams under 50 people.",
  },
  {
    n: 5,
    title: "Setup time for a lean team",
    body: "A CDP that requires 6 weeks of professional services before the first segment goes live is not suitable for a team of three. Time-to-first-activation matters more than the feature list on a comparison page.",
  },
  {
    n: 6,
    title: "B2B SaaS vs e-commerce fit",
    body: "Most CDP vendors optimize for e-commerce because purchase history is the clearest training signal. B2B SaaS teams need product usage events, CRM stage data, and trial behavior. Verify whether the tool has B2B SaaS customer case studies.",
  },
];

// ─── CDP type overview table ──────────────────────────────────────────────────

export const CDP_TYPES: CdpTypeRow[] = [
  {
    cdpType: "Conversational",
    aiCapabilities: "Chat qualification, behavioral scoring, AI booking",
    primaryData: "Website events, chat sessions, lead properties",
    activation: "Chat, email, CRM sync",
    bestFor: "B2B SaaS SMB, inbound-first teams",
  },
  {
    cdpType: "Developer",
    aiCapabilities: "Predictions API, computed traits, ML audiences",
    primaryData: "Events via SDKs, server-side APIs",
    activation: "400+ integrations, email, ad audiences",
    bestFor: "Product-led growth, engineering-led teams",
  },
  {
    cdpType: "Composable",
    aiCapabilities: "AI audience builder, reverse ETL, natural language segments",
    primaryData: "Data warehouse (Snowflake, BigQuery, Redshift)",
    activation: "CRM, ad platforms, email, push",
    bestFor: "Data-mature teams with existing warehouse",
  },
  {
    cdpType: "Enterprise",
    aiCapabilities: "GenAI audience creation, real-time decisioning, predictive scoring",
    primaryData: "First + third party, offline, second party",
    activation: "All channels at scale, real-time triggers",
    bestFor: "Large enterprise, multi-brand, regulated industries",
  },
  {
    cdpType: "Ecommerce",
    aiCapabilities: "AI send-time, predictive CLV, churn risk, product recommendations",
    primaryData: "Purchase history, browsing behavior, email engagement",
    activation: "Email, SMS, push, paid ads, on-site",
    bestFor: "DTC and e-commerce brands, Shopify teams",
  },
];

// ─── Top 3 full tool cards ────────────────────────────────────────────────────

export const TOP_3_TOOLS: TopTool[] = [
  {
    position: 1,
    toolSlug: "dashly",
    toolName: "Dashly",
    bestFor: "Best conversational AI CDP for B2B SaaS teams",
    intro:
      "Dashly builds a unified lead profile for every website visitor, then activates that data through an AI agent that qualifies, books, and nurtures in real time. The data layer captures 30+ behavioral event types, including page visits, feature usage, CRM stage, and pricing page interactions, so the AI agent has full context before a conversation starts. Dashly customers report 82% conversation-to-meeting conversion (InfluADS, 90-day case study) when the AI agent engages within 30 seconds of a pricing page visit.",
    pros: [
      "Unified lead profile tracks 30+ behavioral events, giving the AI agent context before the first message. No manual segment setup required.",
      "AI agent qualifies, books a meeting, and updates the CRM in a single conversation, without a human handoff.",
      "Responds to inbound leads in under 30 seconds, before intent fades and before a competitor chat catches the same visitor.",
      "Transparent self-serve pricing with a free trial. No professional services fee to activate first segments.",
    ],
    cons: [
      "Works best on sites with 5,000+ monthly visitors; teams with lower traffic see slower ROI (per G2 reviews).",
      "CDP layer covers website and product events only; no data warehouse integration for teams running Snowflake or BigQuery (per G2 reviews).",
    ],
    startingPrice: "$500/mo",
    pricingFacts: {
      pricingModel: "Seat-based + feature tiers",
      freeTrial: "14-day free trial",
      enterpriseNote: "Custom for enterprise volume",
    },
    outcomesLine:
      "82% conversation-to-meeting at InfluADS over 90 days. 60-90% show-up rate across B2B SaaS customers.",
    g2Rating: "4.8 (450+ reviews)",
    profileUrl: "/tools/ai-sdr-tools/dashly/",
  },
  {
    position: 2,
    toolSlug: "segment",
    toolName: "Segment (Twilio)",
    bestFor: "Best developer CDP with AI Predictions for PLG teams",
    intro:
      "Segment collects events from any source (web, mobile, server), unifies them into a single customer profile, and distributes to 400+ downstream tools. The Predictions feature uses machine learning to score users against custom goals, such as likelihood to convert to paid or churn risk, without requiring a data science team. Twilio Engage layers email and SMS activation on top of the CDP data, completing the collect-analyze-activate loop.",
    pros: [
      "400+ native integrations cover virtually any stack without custom code; most downstream tools connect in under 30 minutes.",
      "Predictions API scores users against custom goals without a data science team; documented model accuracy available per prediction.",
      "Free Connections plan covers basic event tracking for early-stage teams before committing to a paid tier.",
    ],
    cons: [
      "Price scales sharply with event volume; teams exceeding 10M monthly tracked users report significant cost increases (per G2 reviews).",
      "Complex permission model; medium and large teams frequently cite misconfigured access as a recurring issue (per G2 reviews).",
      "Support response times on the Connections plan are slow; dedicated support requires the enterprise plan (per G2 reviews).",
    ],
    startingPrice: "Free / from $120/mo",
    pricingFacts: {
      pricingModel: "Usage-based (MTUs + events)",
      freeTrial: "Free Connections plan",
      enterpriseNote: "Custom pricing for large volume",
    },
    g2Rating: "4.6 (540+ reviews)",
  },
  {
    position: 3,
    toolSlug: "hightouch",
    toolName: "Hightouch",
    bestFor: "Best composable CDP for data-first teams with a warehouse",
    intro:
      "Hightouch reads data directly from your existing data warehouse and activates it to any downstream tool without moving data through a separate CDP pipeline. The AI Audience Builder lets marketers create segments from natural language descriptions without writing SQL. Reviewers consistently cite the speed from warehouse query to live audience sync as the main differentiator over traditional CDP pipelines.",
    pros: [
      "Connects directly to Snowflake, BigQuery, and Redshift; no separate data pipeline required to sync warehouse data to CRM.",
      "AI Audience Builder creates segments from natural language descriptions, making warehouse data accessible to non-technical marketers.",
      "Free Developer plan covers basic reverse ETL to one destination without a paid commitment.",
    ],
    cons: [
      "Requires an existing data warehouse; teams without one cannot use the core product (per G2 reviews).",
      "Setup and maintenance require RevOps or data team support; solo marketers without SQL knowledge hit a wall quickly (per G2 reviews).",
    ],
    startingPrice: "Free / from $350/mo",
    pricingFacts: {
      pricingModel: "Usage-based (rows synced)",
      freeTrial: "Free Developer plan",
      enterpriseNote: "Custom for high-volume syncs",
    },
    g2Rating: "4.7 (180+ reviews)",
  },
];

// ─── More tools (7 lighter cards) ────────────────────────────────────────────

export const MORE_TOOLS: MoreTool[] = [
  {
    toolSlug: "adobe-rtcdp",
    toolName: "Adobe Real-Time CDP",
    cdpType: "Enterprise",
    tagline:
      "Enterprise AI CDP with GenAI audience creation and real-time profile activation inside Adobe Experience Platform.",
    startingPrice: "Custom ($40K+/year)",
    g2Rating: "4.1",
    pros: [
      "Real-time edge segmentation updates profiles within milliseconds of an event, enabling true real-time personalization.",
      "GenAI audience creation drafts segments from natural language inputs inside Adobe Experience Platform.",
    ],
    cons: [
      "Implementation typically runs 6 to 12 months and requires a certified Adobe partner or dedicated internal team (per G2 reviews).",
      "Total cost of ownership often reaches $200K+ in year one when professional services are included (per G2 reviews).",
    ],
    bestFor:
      "Enterprise teams already on Adobe Experience Cloud with an in-house data engineering team",
  },
  {
    toolSlug: "bloomreach",
    toolName: "Bloomreach",
    cdpType: "Ecommerce",
    tagline:
      "E-commerce AI CDP that unifies customer and product data, then drives personalized experiences across every channel.",
    startingPrice: "Custom",
    g2Rating: "4.7",
    pros: [
      "Combines product catalog intelligence with customer behavioral data for recommendations that account for real-time inventory.",
      "Built-in personalization engine adapts web content, email, and ads from a single data layer without a separate tool.",
    ],
    cons: [
      "Onboarding averages 8 to 12 weeks and typically requires a dedicated e-commerce operations resource (per G2 reviews).",
      "B2B SaaS teams outside retail or DTC find limited industry-specific use cases in the platform (per G2 reviews).",
    ],
    bestFor:
      "Mid-market and enterprise e-commerce teams that need CDP and personalization in one platform",
  },
  {
    toolSlug: "klaviyo",
    toolName: "Klaviyo",
    cdpType: "Ecommerce",
    tagline:
      "E-commerce CDP with AI send-time optimization, predictive CLV, and churn risk scoring. Most-used CDP for Shopify brands.",
    startingPrice: "Free / from $45/mo",
    g2Rating: "4.6",
    pros: [
      "Predictive analytics flags customers likely to churn 14 to 30 days before they leave, giving teams a window to re-engage.",
      "Free plan covers 250 contacts and email sending, the lowest-risk CDP entry point for e-commerce startups.",
    ],
    cons: [
      "Pricing scales sharply with list size; moving from 10K to 100K contacts can push monthly cost above $700/mo (per G2 reviews).",
      "CDP data layer is e-commerce specific; B2B SaaS teams find limited support for product usage events or CRM stage data (per G2 reviews).",
    ],
    bestFor:
      "E-commerce and DTC brands on Shopify or Magento that need CDP + email + SMS in one tool",
  },
  {
    toolSlug: "salesforce-data-cloud",
    toolName: "Salesforce Data Cloud",
    cdpType: "Enterprise",
    tagline:
      "CRM-native AI CDP that unifies customer data across all Salesforce clouds and activates it through Einstein AI.",
    startingPrice: "Custom",
    g2Rating: "4.2",
    pros: [
      "Native to the Salesforce ecosystem; no data sync layer required for teams already on Sales, Service, or Marketing Cloud.",
      "Einstein AI builds predictive models inside the platform without a separate ML tool or data science team.",
    ],
    cons: [
      "Requires Salesforce. Teams on HubSpot or another CRM cannot use it without a major stack change (per G2 reviews).",
      "Typical contracts start at $108K/year before add-ons; cost puts it out of reach for most teams below $30M ARR (per G2 reviews).",
    ],
    bestFor:
      "Enterprise B2B teams fully committed to the Salesforce ecosystem across Sales, Service, and Marketing Cloud",
  },
  {
    toolSlug: "mparticle",
    toolName: "mParticle",
    cdpType: "Enterprise",
    tagline:
      "Enterprise CDP focused on mobile-first data collection with cross-platform identity resolution and server-side filtering.",
    startingPrice: "Custom",
    g2Rating: "4.3",
    pros: [
      "Best-in-class mobile SDK support covers iOS, Android, React Native, and Flutter with a single integration point.",
      "Server-side data filtering validates and cleans events before they reach downstream tools, reducing CRM data quality issues.",
    ],
    cons: [
      "Enterprise-only pricing with no self-serve option; teams cannot test the product without a sales conversation (per G2 reviews).",
      "Non-technical marketers typically need engineering support to set up initial data flows; documentation is dense (per G2 reviews).",
    ],
    bestFor:
      "Mobile-first companies (apps, gaming, fintech) with high event volumes and dedicated mobile engineering teams",
  },
  {
    toolSlug: "tealium",
    toolName: "Tealium",
    cdpType: "Enterprise",
    tagline:
      "Enterprise CDP with tag management, AudienceStream AI predictions, and consent management built in.",
    startingPrice: "Custom",
    g2Rating: "4.5",
    pros: [
      "AudienceStream machine learning scores visitors in real time without sending data to external ML services.",
      "Consent management layer handles GDPR and CCPA compliance inside the same platform as the CDP.",
    ],
    cons: [
      "Tag management legacy architecture adds complexity for teams that do not need a TMS alongside the CDP (per G2 reviews).",
      "Implementation typically requires 3 to 6 months and a certified partner for mid-size enterprise deployments (per G2 reviews).",
    ],
    bestFor:
      "Enterprise teams with tag management needs and regulatory compliance requirements across multiple markets",
  },
  {
    toolSlug: "simon-data",
    toolName: "Simon Data",
    cdpType: "Enterprise",
    tagline:
      "Marketer-friendly CDP with a drag-and-drop audience builder and ML-powered journey orchestration.",
    startingPrice: "Custom",
    g2Rating: "4.5",
    pros: [
      "Drag-and-drop segment builder lets marketers build ML-powered audiences without SQL or data science support.",
      "Journey orchestration runs multi-step campaigns from a single data layer, reducing time from insight to send.",
    ],
    cons: [
      "No public pricing tiers; teams need a sales call to get any cost estimate (per G2 reviews).",
      "Integration library is smaller than Segment or Tealium; niche tools may need custom API work (per G2 reviews).",
    ],
    bestFor:
      "Mid-market teams that want ML-powered audiences without requiring a data science team or SQL expertise",
  },
];

// ─── Use case cards (2x2 grid) ────────────────────────────────────────────────

export const USE_CASES: UseCaseCard[] = [
  {
    id: "b2b-saas",
    color: "#4c6fff",
    label: "B2B SaaS",
    heading: "Best AI CDPs for B2B SaaS teams",
    body: "B2B SaaS teams need a CDP that reads product usage events, trial behavior, and CRM stage, not just purchase history. These three tools cover inbound qualification, data warehouse activation, and developer-friendly event tracking for PLG and sales-assisted motions.",
    tools: [
      "Dashly (conversational, inbound conversion)",
      "Segment (developer, PLG)",
      "Hightouch (warehouse activation, RevOps)",
    ],
  },
  {
    id: "ecommerce",
    color: "#ff6b35",
    label: "E-commerce",
    heading: "Best AI CDPs for e-commerce brands",
    body: "E-commerce CDPs shine on purchase history, browse abandonment, and predictive CLV. Both tools cover the Shopify ecosystem and drive activation through email, SMS, and paid ad audiences without a separate activation tool.",
    tools: [
      "Bloomreach (personalization engine, mid-market+)",
      "Klaviyo (email + CDP, free to start)",
    ],
  },
  {
    id: "enterprise",
    color: "#1c1c1c",
    labelColor: "#ffffff",
    label: "Enterprise",
    heading: "Best AI CDPs for large enterprise",
    body: "Enterprise CDPs handle multi-brand data governance, real-time decisioning at scale, and regulatory compliance. All three options require a dedicated implementation team and a 6 to 12 month onboarding timeline.",
    tools: [
      "Adobe Real-Time CDP (Experience Platform ecosystem)",
      "Salesforce Data Cloud (Salesforce-native)",
      "mParticle (mobile-first, high event volume)",
    ],
  },
  {
    id: "data-first",
    color: "#d0ff37",
    label: "Data-first",
    heading: "Best composable CDPs for data teams",
    body: "Composable CDPs read from an existing data warehouse and push to downstream tools without a separate pipeline. These two suit RevOps and data engineering teams already managing a Snowflake or BigQuery environment.",
    tools: [
      "Hightouch (AI audience builder, reverse ETL)",
      "Tealium (AI predictions + consent management)",
    ],
  },
];

// ─── Stage guide (3 cards) ────────────────────────────────────────────────────

export const STAGE_GUIDE: StageCard[] = [
  {
    stage: "startup",
    label: "Solo marketer / startup",
    color: "#d0ff37",
    heading: "Start with a self-serve CDP you can activate in a day.",
    body: "Before your stack has a data warehouse or a dedicated data team, a CDP that runs on tag-based event collection with a free plan is the right starting point. Focus on activation, not architecture.",
    tools: [
      "Dashly (free trial, inbound conversion on day one)",
      "Klaviyo (free for 250 contacts, e-commerce teams)",
      "Segment Connections (free, developer teams)",
    ],
    watch_out:
      "Avoid: Adobe, Salesforce Data Cloud, mParticle. The 6 to 12 month onboarding timeline is not justified before product-market fit.",
  },
  {
    stage: "mid-market",
    label: "Mid-market data team",
    color: "#ff6b35",
    heading: "Layer in warehouse-native activation once the data team is in place.",
    body: "With a data engineer or RevOps person on the team, composable CDPs unlock the full value of your existing Snowflake or BigQuery data. Add an AI segmentation layer on top of a developer CDP if you need downstream activation flexibility.",
    tools: [
      "Segment (developer CDP, 400+ destinations)",
      "Hightouch (reverse ETL from existing warehouse)",
      "Bloomreach (if e-commerce is the primary channel)",
    ],
    watch_out:
      "Avoid Hightouch without a data warehouse. The product is built for teams already running Snowflake or BigQuery.",
  },
  {
    stage: "enterprise",
    label: "Enterprise / CDP specialist",
    color: "#4c6fff",
    heading: "Commit to a platform, not just a tool.",
    body: "At enterprise scale, the CDP is infrastructure. Adobe, Salesforce Data Cloud, and mParticle all require dedicated implementation teams and multi-year contracts. Evaluate total cost of ownership, not just the license price.",
    tools: [
      "Adobe Real-Time CDP (Adobe Experience Cloud teams)",
      "Salesforce Data Cloud (full Salesforce ecosystem)",
      "mParticle (mobile-first, high-volume event processing)",
      "Tealium (tag management + CDP + consent management)",
    ],
    watch_out:
      "TCO matters: Adobe + implementation can reach $300K+ in year one when professional services are included.",
  },
];

// ─── FAQ items ────────────────────────────────────────────────────────────────

export const FAQ_ITEMS: FaqItem[] = [
  {
    question: "What is an AI CDP?",
    answer:
      "In this article, CDP stands for Customer Data Platform, not Climate Disclosure Project or Continuous Data Protection. An AI CDP is a Customer Data Platform that uses machine learning to go beyond data collection and unification. Traditional CDPs collect first-party data and build unified customer profiles. AI CDPs add predictive scoring (churn risk, conversion likelihood, CLV), AI-powered audience creation, and agentic activation that triggers personalized conversations in real time based on model output.",
  },
  {
    question: "What is the best AI CDP for B2B SaaS?",
    answer:
      "For B2B SaaS teams, the best AI CDP depends on your motion and team size. Dashly covers inbound qualification and real-time AI activation for teams under 500 people, with transparent self-serve pricing. Segment suits product-led growth teams that need developer-friendly event collection and 400+ downstream integrations. Hightouch is right for RevOps teams with an existing data warehouse who want to activate CRM and warehouse data without rebuilding a CDP pipeline. Enterprise CDPs (Adobe, Salesforce) are not cost-justified for most teams below $30M ARR.",
  },
  {
    question: "How does an AI CDP differ from a traditional CDP?",
    answer:
      "A traditional CDP collects first-party data and builds unified customer profiles. It answers: who is this person and what have they done? An AI CDP adds a machine learning layer that answers: what will this person do next, and how do we act on that prediction before they do it? The difference shows up in segmentation (rule-based vs. predictive), activation (batch export vs. real-time triggers), and personalization (template-based vs. model-driven content).",
  },
  {
    question: "What is a real-time CDP?",
    answer:
      "A real-time CDP processes events as they happen, typically within seconds, and updates the unified customer profile immediately. This matters when a B2B SaaS trial user visits the pricing page three times in one session: a real-time CDP triggers the AI agent within 30 seconds of the third visit, while a batch CDP waits until the nightly data load. Adobe Real-Time CDP and Dashly both operate in real-time. Segment processes events in near-real-time, seconds to minutes depending on plan.",
  },
  {
    question: "What is the best AI CDP for small businesses?",
    answer:
      "For small businesses and early-stage startups, Klaviyo (e-commerce) and Dashly (B2B SaaS) are the two best AI CDPs with self-serve pricing and no implementation fee. Klaviyo starts free for up to 250 contacts and is the most-used CDP for Shopify brands. Dashly starts at $500/mo and covers inbound qualification for B2B SaaS teams. Segment's free Connections plan works for developer-led teams that want event tracking without committing to a paid CDP. Avoid enterprise CDPs until you have a dedicated data team.",
  },
  {
    question: "Is Dashly a CDP?",
    answer:
      "Dashly is a conversational AI CDP: it collects and activates customer data through an AI agent, not through a traditional data warehouse pipeline. It captures 30+ behavioral event types, builds unified lead profiles, and uses that data to qualify, book, and nurture leads in real time. It is not a replacement for an enterprise CDP like Segment or Adobe Real-Time CDP if you need a data warehouse integration or 400+ downstream destinations. For B2B SaaS teams that need inbound AI activation on top of a customer data layer, Dashly covers both without requiring a separate activation tool.",
  },
  {
    question: "How much does an AI CDP cost?",
    answer:
      "Self-serve AI CDPs start at free (Segment Connections, Hightouch Developer, Klaviyo up to 250 contacts) and scale to $45/mo (Klaviyo), $120/mo (Segment), $350/mo (Hightouch), and $500/mo (Dashly). Mid-market CDPs like Bloomreach and Simon Data are custom-priced, typically $2,000 to $5,000/mo. Enterprise CDPs (Adobe Real-Time CDP, Salesforce Data Cloud, mParticle, Tealium) are enterprise contracts, often $40,000 to $200,000+ per year. The biggest hidden cost across all tiers is implementation: budget 2 to 4 weeks of team time before the first AI segment activates.",
  },
];
